import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetCasRegistryNumberQuery as GetCasRegistryNumberQueryResponse,
  GetCasRegistryNumberQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetCasRegistryNumberQuery';
import {GET_CAS_REGISTRY_NUMBER_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {
  CustomResolutionQueryType,
  GetCasRegistryNumberQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

export async function bulkResolveGetCasRegistryNumberToServerResolutionResult(
  queries: Array<GetCasRegistryNumberQuery>
): Promise<Array<ServerResolutionResult>> {
  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<
    GetCasRegistryNumberQueryResponse,
    GetCasRegistryNumberQueryVariables
  >(
    {
      query: GET_CAS_REGISTRY_NUMBER_QUERY,
    },
    {variables: {moleculeIds}}
  );

  // Build a map of moleculeId -> CAS number for efficient lookup
  const casNumberByMoleculeId = new Map<string, string | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      casNumberByMoleculeId.set(molecule.invalidId, null);
    } else {
      const casRegistryNumber = molecule.currentVersion.chemicalStructure.casRegistryNumber;
      if (casRegistryNumber.__typename === 'CasRegistryNumberNode') {
        casNumberByMoleculeId.set(molecule.id, casRegistryNumber.value);
      } else {
        casNumberByMoleculeId.set(molecule.id, null);
      }
    }
  }

  // Return results in the same order as the input queries
  return queries.map((query) => {
    const casNumber = casNumberByMoleculeId.get(query.moleculeId);
    if (casNumber === undefined) {
      // Molecule was not returned by the server (e.g. deleted between query construction and execution)
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve CAS number');
    }
    if (casNumber == null) {
      // Molecule exists but has no CAS number in PubChem — leave cell empty, no error
      return makeCustomResolutionResultFromTypedValue(null);
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.TEXT,
      value: casNumber,
    });
  });
}

export function getCasRegistryNumberColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The molecule ID comes from the first cell dependency (the Name column containing the bioentity)
  const bioentityTypedValue = cellDependencies[0];
  // If there's no bioentity in the dependency, return empty
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }
  const moleculeId = bioentityTypedValue.bioentitySummary.id;
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_CAS_REGISTRY_NUMBER,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
